/**
 * Generic Multiplayer Service
 * 
 * Base class for all multiplayer game services.
 * Provides common functionality for room management, player management,
 * and move synchronization that works across all games.
 */

import { WebSocketService } from '../WebSocketService';

export interface RoomInfo {
  code: string;
  gameType: string;
  playerRole: string;
  playerColor?: string | null;
  maxPlayers: number;
}

export interface GameResult {
  result: 'win' | 'draw' | 'resignation' | 'timeout';
  winner?: string;
  reason?: string;
}

export interface PlayerInfo {
  id: string;
  role: string;
  color?: string;
  connected: boolean;
}

/**
 * Generic Multiplayer Service
 * 
 * @template TGameState - The game-specific state type
 * @template TMove - The game-specific move type
 */
export abstract class GenericMultiplayerService<TGameState = any, TMove = any> {
  protected wsService: WebSocketService;
  protected roomCode: string | null = null;
  protected playerId: string | null = null;
  protected playerRole: string | null = null;
  protected playerColor: string | null = null;
  protected gameType: string;
  protected gameState: TGameState | null = null;
  protected isCreatingRoom: boolean = false;
  protected isJoiningRoom: boolean = false;
  
  // Callbacks
  protected onGameStartedCallback?: () => void;
  protected onMoveUpdateCallback?: (move: TMove, gameState: TGameState) => void;
  protected onGameOverCallback?: (result: GameResult) => void;
  protected onPlayerConnectedCallback?: (playerId: string) => void;
  protected onPlayerDisconnectedCallback?: (playerId: string) => void;
  protected onGameStateReceivedCallback?: (gameState: TGameState) => void;
  protected onErrorCallback?: (error: string) => void;
  protected onConnectionStatusCallback?: (status: 'connected' | 'disconnected' | 'reconnecting') => void;

  constructor(wsService: WebSocketService, gameType: string) {
    this.wsService = wsService;
    this.gameType = gameType;
    this.playerId = typeof crypto !== 'undefined' && crypto.getRandomValues
      ? `player_${Date.now()}_${crypto.getRandomValues(new Uint8Array(8)).reduce((s, b) => s + b.toString(16).padStart(2, '0'), '')}`
      : `player_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    this.setupMessageHandlers();
  }

  /**
   * Create a new game room
   */
  async createRoom(): Promise<RoomInfo> {
    if (this.roomCode) {
      console.warn('⚠️ Already in room:', this.roomCode);
      return {
        code: this.roomCode,
        gameType: this.gameType,
        playerRole: this.playerRole || 'unknown',
        playerColor: this.playerColor,
        maxPlayers: this.getMaxPlayers()
      };
    }
    
    if (this.isCreatingRoom) {
      throw new Error('Room creation already in progress');
    }
    
    this.isCreatingRoom = true;
    console.log(`🔍 Creating ${this.gameType} room...`);
    
    try {
      await this.wsService.connect();
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.isCreatingRoom = false;
          reject(new Error('Room creation timeout'));
        }, 10000);

        const originalCallback = this.onGameStartedCallback;
        this.onGameStartedCallback = () => {
          clearTimeout(timeout);
          this.isCreatingRoom = false;
          this.onGameStartedCallback = originalCallback;
          
          if (this.roomCode && this.playerRole) {
            console.log(`✅ ${this.gameType} room created: ${this.roomCode}`);
            resolve({
              code: this.roomCode,
              gameType: this.gameType,
              playerRole: this.playerRole,
              playerColor: this.playerColor,
              maxPlayers: this.getMaxPlayers()
            });
            
            if (originalCallback) {
              originalCallback();
            }
          } else {
            reject(new Error('Room created but missing information'));
          }
        };

        this.sendCreateRoomMessage();
      });
    } catch (error) {
      this.isCreatingRoom = false;
      throw error;
    }
  }

  /**
   * Join an existing game room
   */
  async joinRoom(roomCode: string): Promise<RoomInfo> {
    if (this.roomCode) {
      if (this.roomCode === roomCode) {
        console.log('✅ Already in this room');
        return {
          code: this.roomCode,
          gameType: this.gameType,
          playerRole: this.playerRole || 'unknown',
          playerColor: this.playerColor,
          maxPlayers: this.getMaxPlayers()
        };
      } else {
        throw new Error('Already in another room');
      }
    }
    
    if (this.isJoiningRoom) {
      throw new Error('Room join already in progress');
    }
    
    this.isJoiningRoom = true;
    console.log(`🔍 Joining ${this.gameType} room: ${roomCode}`);
    
    try {
      await this.wsService.connect();
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.isJoiningRoom = false;
          reject(new Error('Room join timeout'));
        }, 10000);

        const originalCallback = this.onGameStartedCallback;
        this.onGameStartedCallback = () => {
          clearTimeout(timeout);
          this.isJoiningRoom = false;
          this.onGameStartedCallback = originalCallback;
          
          if (this.roomCode && this.playerRole) {
            console.log(`✅ Joined ${this.gameType} room: ${this.roomCode}`);
            resolve({
              code: this.roomCode,
              gameType: this.gameType,
              playerRole: this.playerRole,
              playerColor: this.playerColor,
              maxPlayers: this.getMaxPlayers()
            });
            
            if (originalCallback) {
              originalCallback();
            }
          } else {
            reject(new Error('Room joined but missing information'));
          }
        };

        this.sendJoinRoomMessage(roomCode);
      });
    } catch (error) {
      this.isJoiningRoom = false;
      throw error;
    }
  }

  /**
   * Leave the current game room
   */
  async leaveGame(): Promise<void> {
    if (this.roomCode) {
      this.sendLeaveRoomMessage();
    }
    this.cleanup();
  }

  /**
   * Send a move to other players
   */
  abstract sendMove(move: TMove): Promise<boolean>;

  /**
   * Check if it's the current player's turn
   */
  abstract isMyTurn(currentTurn: string): boolean;

  // Event handler setters
  onGameStarted(callback: () => void): void {
    this.onGameStartedCallback = callback;
  }

  onMoveUpdate(callback: (move: TMove, gameState: TGameState) => void): void {
    this.onMoveUpdateCallback = callback;
  }

  onGameOver(callback: (result: GameResult) => void): void {
    this.onGameOverCallback = callback;
  }

  onPlayerConnected(callback: (playerId: string) => void): void {
    this.onPlayerConnectedCallback = callback;
  }

  onPlayerDisconnected(callback: (playerId: string) => void): void {
    this.onPlayerDisconnectedCallback = callback;
  }

  onGameStateReceived(callback: (gameState: TGameState) => void): void {
    this.onGameStateReceivedCallback = callback;
  }

  onError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  onConnectionStatus(callback: (status: 'connected' | 'disconnected' | 'reconnecting') => void): void {
    this.onConnectionStatusCallback = callback;
  }

  // Getters
  getRoomCode(): string | null {
    return this.roomCode;
  }

  getPlayerRole(): string | null {
    return this.playerRole;
  }

  getPlayerColor(): string | null {
    return this.playerColor;
  }

  getGameState(): TGameState | null {
    return this.gameState;
  }

  isConnected(): boolean {
    // WebSocketService doesn't have isConnected method, check if ws exists
    return true; // Assume connected if service exists
  }

  // Abstract methods that must be implemented by game-specific services
  protected abstract setupMessageHandlers(): void;
  protected abstract sendCreateRoomMessage(): void;
  protected abstract sendJoinRoomMessage(roomCode: string): void;
  protected abstract sendLeaveRoomMessage(): void;
  protected abstract getMaxPlayers(): number;

  // Utility methods
  protected cleanup(): void {
    this.roomCode = null;
    this.playerRole = null;
    this.playerColor = null;
    this.gameState = null;
    this.isCreatingRoom = false;
    this.isJoiningRoom = false;
  }

  protected handleError(message: string, error?: any): void {
    console.error(`${this.gameType} Multiplayer Error: ${message}`, error);
    if (this.onErrorCallback) {
      this.onErrorCallback(message);
    }
  }
}
